import { NavItem } from '@/types';

const IS_DEV_TOOLS_ENABLED = process.env.NEXT_PUBLIC_DEV_TOOLS !== 'false';

/**
 * Navigation configuration
 * Used by sidebar and Cmd+K bar.
 * `titleKey` values are translation keys resolved at render time via `t()`.
 * `access.requiredPermissions` controls RBAC visibility (OR logic).
 * `group` determines which sidebar section the item belongs to.
 */

export const NAV_GROUP_ORDER = [
  'externalLab',
  'internalLab',
  'logisticsAndSecurity',
  'administration',
  'systemSettings'
] as const;

export type NavGroup = (typeof NAV_GROUP_ORDER)[number];

export const NAV_GROUP_LABELS: Record<NavGroup, string> = {
  externalLab: 'navigation.sidebar.externalLab',
  internalLab: 'navigation.sidebar.internalLab',
  logisticsAndSecurity: 'navigation.sidebar.logisticsAndSecurity',
  administration: 'navigation.sidebar.administration',
  systemSettings: 'navigation.sidebar.systemSettings'
};

export const navItems: NavItem[] = [
  {
    title: 'Main Dashboard',
    titleKey: 'navigation.sidebar.mainDashboard',
    url: '/dashboard',
    icon: 'dashboard',
    isActive: false,
    shortcut: ['d', 'd'],
    items: [],
    access: { requiredPermissions: ['dashboard.view'] },
    group: 'administration'
  },
  {
    title: 'AI Search',
    titleKey: 'navigation.sidebar.aiSearch',
    url: '/dashboard/search',
    icon: 'search',
    isActive: false,
    shortcut: ['s', 's'],
    items: [],
    access: { requiredPermissions: ['search.view'] },
    group: 'administration'
  },
  {
    title: 'Registration',
    titleKey: 'navigation.sidebar.registration',
    url: '/dashboard/registration',
    icon: 'registration',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['registrations.view'] },
    group: 'externalLab'
  },
  {
    title: 'Page Sticking',
    titleKey: 'navigation.sidebar.sticking',
    url: '/dashboard/sticking',
    icon: 'sticking',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['page_indexes.view'] },
    group: 'externalLab'
  },
  {
    title: 'Review',
    titleKey: 'navigation.sidebar.review',
    url: '/dashboard/review',
    icon: 'review',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['review.view'] },
    group: 'externalLab'
  },
  {
    title: 'Packing',
    titleKey: 'navigation.sidebar.packing',
    url: '/dashboard/packing',
    icon: 'packing',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['boxes.view'] },
    group: 'externalLab'
  },
  {
    title: 'Shipping',
    titleKey: 'navigation.sidebar.shipping',
    url: '/dashboard/shipping',
    icon: 'shipping',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['shipments.view'] },
    group: 'externalLab'
  },

  // --- Internal Lab ---
  {
    title: 'Box Reception & Inspection',
    titleKey: 'navigation.sidebar.boxReception',
    url: '/dashboard/shipment-statement',
    icon: 'boxReception',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['reception.view'] },
    group: 'internalLab'
  },
  {
    title: 'Sorting',
    titleKey: 'navigation.sidebar.sorting',
    url: '/dashboard/sorting',
    icon: 'packing',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['sorting.view'] },
    group: 'internalLab'
  },
  {
    title: 'Scan Assignment',
    titleKey: 'navigation.sidebar.handoverScanning',
    url: '/dashboard/handover-scanning',
    icon: 'handoverScanning',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['handover_scanning.view'] },
    group: 'internalLab'
  },
  {
    title: 'Scan Upload',
    titleKey: 'navigation.sidebar.photoScanning',
    url: '/dashboard/photo-scanning',
    icon: 'camera',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['photo_scanning.view'] },
    group: 'internalLab'
  },
  {
    title: 'QA Review',
    titleKey: 'navigation.sidebar.qrReview',
    url: '/dashboard/qr-review',
    icon: 'review',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['qr_review.view'] },
    group: 'internalLab'
  },
  {
    title: 'OCR Containers',
    titleKey: 'navigation.sidebar.ocrContainers',
    url: '/dashboard/ocr-containers',
    icon: 'formManagement',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['ocr.view'] },
    group: 'internalLab'
  },
  {
    title: 'QA OCR',
    titleKey: 'navigation.sidebar.qaOcr1',
    url: '/dashboard/qa-ocr-1',
    icon: 'review',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['qa1_ocr.view'] },
    group: 'internalLab'
  },
  {
    title: 'QA 2 OCR',
    titleKey: 'navigation.sidebar.qaOcr2',
    url: '/dashboard/qa-ocr-2',
    icon: 'review',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['qa2_ocr.view'] },
    group: 'internalLab'
  },
  {
    title: 'QA 3 OCR & Scanning',
    titleKey: 'navigation.sidebar.qaOcr3',
    url: '/dashboard/qa-ocr-3',
    icon: 'review',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['qa3_ocr.view'] },
    group: 'internalLab'
  },
  {
    title: 'Warehouse',
    titleKey: 'navigation.sidebar.warehouse',
    url: '/dashboard/warehouse',
    icon: 'warehouseAssignment',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['warehouse.operate'] },
    group: 'internalLab'
  },
  // Processing Management (OMA-617) moved here from its own top-level group,
  // which is now gone — it held only this item and Processing Settings, and
  // both belong under an existing section. Routes and permissions unchanged.
  {
    title: 'Repair Management',
    titleKey: 'navigation.sidebar.repairManagement',
    url: '/dashboard/repairs',
    icon: 'wrench',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['repairs.view'] },
    group: 'internalLab'
  },
  // --- Administration ---
  // "Enterprise Clients" (OMA-591) is intentionally absent: the route still
  // exists, it is just no longer surfaced in the sidebar or the Cmd+K bar.
  {
    title: 'Supervisor Dashboard',
    titleKey: 'navigation.sidebar.supervisor',
    url: '/dashboard/supervisor',
    icon: 'supervisor',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['supervisor.view'] },
    group: 'administration'
  },

  // --- System Settings ---
  // Form Management and OCR Templates live here rather than under
  // Administration (OMA-591) — routes and permissions are unchanged.
  // Processing Settings (OMA-617) moved here from the retired Processing
  // Management group. Route and permission unchanged.
  {
    title: 'Repair Settings',
    titleKey: 'navigation.sidebar.repairSettings',
    url: '/dashboard/repair-settings',
    icon: 'settings',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['repair_settings.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Form Management',
    titleKey: 'navigation.sidebar.formManagement',
    url: '/dashboard/form-management',
    icon: 'formManagement',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['dynamic_forms.view'] },
    group: 'systemSettings'
  },
  {
    title: 'OCR Templates',
    titleKey: 'navigation.sidebar.ocrTemplates',
    url: '/dashboard/ocr-templates',
    icon: 'formManagement',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['ocr_templates.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Roles Management',
    titleKey: 'navigation.sidebar.rolesManagement',
    url: '/dashboard/roles',
    icon: 'roles',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['roles.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Users Management',
    titleKey: 'navigation.sidebar.usersManagement',
    url: '/dashboard/users',
    icon: 'users',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['users.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Storage Locations',
    titleKey: 'navigation.sidebar.storageLocations',
    url: '/dashboard/storage-locations',
    icon: 'storageLocations',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['storage_locations.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Shelves',
    titleKey: 'navigation.sidebar.shelves',
    url: '/dashboard/shelves',
    icon: 'storageLocations',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['shelves.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Box Types',
    titleKey: 'navigation.sidebar.boxTypes',
    url: '/dashboard/box-types',
    icon: 'boxTypes',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['box_types.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Container Types',
    titleKey: 'navigation.sidebar.containerTypes',
    url: '/dashboard/container-types',
    icon: 'containerTypes',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['container_types.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Devices',
    titleKey: 'navigation.sidebar.devices',
    url: '/dashboard/devices',
    icon: 'devices',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['devices.view'] },
    group: 'systemSettings'
  },
  {
    title: 'System Logs',
    titleKey: 'navigation.sidebar.systemLogs',
    url: '/dashboard/system-logs',
    icon: 'systemLogs',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['system_logs.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Edit Localization',
    titleKey: 'navigation.sidebar.localization',
    url: '/dashboard/localization',
    icon: 'languages',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['localization.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Configurations',
    titleKey: 'navigation.sidebar.configurations',
    url: '/dashboard/configurations',
    icon: 'settings',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['configurations.view'] },
    group: 'systemSettings'
  },
  {
    title: 'AI Models',
    titleKey: 'navigation.sidebar.aiModels',
    url: '/dashboard/ai-models',
    icon: 'settings',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['ai_models.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Rejection Reasons',
    titleKey: 'navigation.sidebar.rejectionReasons',
    url: '/dashboard/rejection-reasons',
    icon: 'rejectionReasons',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['rejection_reasons.view'] },
    group: 'systemSettings'
  },
  {
    title: 'Labs',
    titleKey: 'navigation.sidebar.labs',
    url: '/dashboard/labs',
    icon: 'labs',
    isActive: false,
    items: [],
    access: { requiredPermissions: ['labs.view'] },
    group: 'systemSettings'
  },
  {
    title: 'About',
    titleKey: 'navigation.sidebar.about',
    url: '/dashboard/about',
    icon: 'about',
    isActive: false,
    items: [],
    group: 'systemSettings'
  }
  // ...(IS_DEV_TOOLS_ENABLED
  //   ? [
  //       {
  //         title: 'Dev Tools',
  //         titleKey: 'navigation.sidebar.devTools',
  //         url: '/dashboard/dev-tools',
  //         icon: 'wrench',
  //         isActive: false,
  //         items: [],
  //         group: 'systemSettings'
  //       } as NavItem
  //     ]
  //   : [])
];
